import * as Sentry from "@sentry/react";
import { ContactButton } from "@/components/common/contact-button";
import { ModalWrapper } from "./modal-wrapper";
import { Button } from "@/toggle-ui-kit/button";
import {
  ModalCloseOptions,
  ModalInstance,
  popModal,
  removeModal,
  useModalStore,
} from "@/stores/modal";
import { Dialog, DialogContent, DialogTitle } from "@/styles/ui/dialog";

/** 이름과 id 만 보면 되므로 props 타입은 알 필요가 없다. */
type StackedModal = Pick<ModalInstance<unknown>, "id" | "Component">;

/** Sentry 이벤트에서 "어느 모달이 터졌는지"를 바로 알 수 있게 컴포넌트 이름을 태그로 남긴다. */
const modalName = ({ Component, id }: StackedModal) =>
  Component.displayName || Component.name || `unknown(${id})`;

/**
 * 터진 모달 자리에 뜨는 화면. 같은 버튼을 다시 눌러 같은 에러를 반복하지 않도록 문의 경로를
 * 함께 둔다. 닫기는 popModal 이 아니라 id 로 제거한다 — 터진 모달이 최상단이 아닐 수 있다.
 */
const ModalErrorFallback = ({ modal }: { modal: StackedModal }) => (
  <ModalWrapper className="flex w-[320px] flex-col items-center gap-4 rounded-[10px] bg-white p-6 text-center">
    <div className="flex flex-col gap-1">
      <p className="body-1-normal-regular text-label-normal">
        창을 여는 중 오류가 발생했습니다.
        <br />
        문제가 계속되면 아래 코드와 함께 문의해 주세요.
      </p>
      {/* 문의 시 어느 창인지 특정하는 용도 — Sentry 의 modal 태그와 같은 값이다. */}
      <p className="label-2-regular text-label-alternative">
        {modalName(modal)}
      </p>
    </div>
    <div className="flex items-center gap-2">
      <ContactButton text="문의하기" className="text-label-alternative" />
      <Button variant="outlined" onClick={() => removeModal(modal.id)}>
        닫기
      </Button>
    </div>
  </ModalWrapper>
);

const ModalStack = () => {
  const modalStack = useModalStore((state) => state.modals.stack);

  return (
    <>
      {modalStack.map((modal, index) => {
        const { Component, props, id } = modal;
        const isLast = index === modalStack.length - 1;
        // props 는 ModalInstance<any> 라 그냥 읽으면 오타·이름 변경이 조용히 undefined 가
        // 된다. 닫힘 옵션만 pushModal 과 같은 타입으로 좁혀서 본다.
        const closeOptions: ModalCloseOptions = props ?? {};

        // 닫힘 차단은 아래 onEscapeKeyDown·onPointerDownOutside 에서 제스처별로 한다.
        // 여기서 두 flag 를 함께 보면 "dim 클릭만 막고 ESC 는 허용" 을 표현할 수 없다.
        const handleOpenChange = (open: boolean) => {
          if (!open && isLast) {
            popModal();
          }
        };

        const depth = modalStack.length - 1 - index;

        if (depth > 2) {
          return null;
        }

        return (
          <Dialog key={id} open={true} onOpenChange={handleOpenChange}>
            <DialogContent
              className="bg-transparent p-0 gap-0 border-none shadow-none flex items-center justify-center transition-all duration-300 ease-in-out max-w-none"
              style={{
                filter: `blur(${depth * 1.5}px)`,
                pointerEvents: isLast ? "auto" : "none",
              }}
              onOpenAutoFocus={(e) => e.preventDefault()}
              onPointerDownOutside={(e) => {
                if (closeOptions.closeOnPointerDownOutside === false)
                  e.preventDefault();
              }}
              onEscapeKeyDown={(e) => {
                if (closeOptions.closeOnEscapeKey === false) {
                  e.preventDefault();
                }
              }}
            >
              <DialogTitle className="sr-only">Modal {index}</DialogTitle>
              {/* 모달 하나가 던져도 앱 전체가 흰 화면이 되지 않게 폭발 반경을 이 모달로 한정한다.
                  (provider 계약 위반으로 실제 두 번 터졌다 — providers.tsx 참고) */}
              <Sentry.ErrorBoundary
                beforeCapture={(scope) => {
                  scope.setTag("modal", modalName(modal));
                  scope.setTag("modal.stackDepth", String(modalStack.length));
                }}
                fallback={<ModalErrorFallback modal={modal} />}
              >
                <Component {...props} />
              </Sentry.ErrorBoundary>
            </DialogContent>
          </Dialog>
        );
      })}
    </>
  );
};

export default ModalStack;
